import { usePostHog } from "posthog-js/react";

import { type RoomTier } from "@trip/constants";

interface Base<T extends string> {
  name: T;
}

type Event =
  | (Base<"Change Passenger Count"> & { category: string })
  | (Base<"Change Date"> & { date: string })
  | (Base<"Add To Cart"> & {
      datoTourId?: string;
      pathname: string;
      type: "bokun" | "linktivity";
    })
  | Base<"Start Verify Email">
  | Base<"Attempt Verify Code">
  | Base<"Complete Verification">
  | (Base<"Attempt Payment"> & { amount: number })
  | Base<"Open Accommodation Modal">
  | Base<"Open Transfers Modal">
  | Base<"Open Consultation">
  | (Base<"Book Consultation"> & { timeZone: string })
  | Base<"Open Search">
  | (Base<"Search"> & { from: "homepage"; query: string })
  | (Base<"Change Trip Parameters"> & { parameter: "passengers" | "date" })
  | (Base<"Change Trip Hotel"> & {
      datoHotelId: string;
      datoTripId: string;
      tier: RoomTier;
    })
  | (Base<"Change Destination Nights"> & {
      datoTripId: string;
      destinationId: string;
      nights: number;
      added: number;
    })
  | (Base<"Select Trip Location"> & { locationSlug: string; selected: boolean })
  | Base<"Open Trip Builder">
  | (Base<"Change Trip Date Range"> & {
      startDate: string;
      endDate: string;
      totalNights: number;
    })
  | (Base<"Add Destination"> & {
      locationSlug: string;
      method: "manual" | "assistant" | "bulk-assistant";
    })
  | (Base<"Remove Destination"> & { locationSlug: string })
  | (Base<"Reorder Destinations"> & {
      fromIndex: number;
      toIndex: number;
    })
  | (Base<"Adjust Destination Nights"> & {
      locationSlug: string;
      oldNights: number;
      newNights: number;
    })
  | (Base<"Toggle Trip Assistant"> & { assistantMode: boolean })
  | (Base<"Apply Assistant Suggestion"> & {
      locationSlug: string;
      insertAfterIndex: number;
    })
  | (Base<"Dismiss Assistant Suggestion"> & { locationSlug: string })
  | (Base<"Apply All Assistant Suggestions"> & { suggestionsCount: number })
  | (Base<"Change Trip Passenger Count"> & {
      adults: number;
      children: number;
    })
  | (Base<"Change Child Age"> & { childIndex: number; age: number })
  | (Base<"Create Custom Trip"> & {
      destinationCount: number;
      totalNights: number;
      passengerCount: number;
    });

export function useTrack() {
  const posthog = usePostHog();
  posthog.startSessionRecording();
  return ({ name, ...event }: Event) => posthog.capture(name, { ...event });
}
